import { useCallback, useEffect, useRef, useState } from "react";
import { useFetch } from "../../../utils/hooks/useFetch";
import {
  getApprovalListServiceConfig,
  getApproveRejectTimesheetServiceConfig,
} from "../../../utils/services/timesheet";
import { useToastInfo } from "../../../utils/hooks/useToastInfo";
import { startOfWeek, endOfWeek } from "date-fns";
import { getYearFormatter } from "../../../utils/helpers";
import { translate } from "../../../utils/translations/translate";
import { TIMESHEET_ACTION_STATES } from "../../../utils/constants";
import { useUpdateCount } from "../../Header/updateCount";
export const useTimesheetBulkActions = () => {
  const { isLoading, performAction } = useFetch();
  const [page, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [approvalList, setApprovalList] = useState([]);
  const chunkRef = useRef(null);
  const updateCount = useUpdateCount();
  const [totalRecords, setTotalRecords] = useState(0);
  const { toastInfo, setToastInfo } = useToastInfo();
  const [selectedIds, setSeletedIds] = useState([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalModalInfo, setApprovalModalInfo] = useState(null);
  const [week, setWeek] = useState({
    firstDay: startOfWeek(new Date(), { weekStartsOn: 1 }),
    lastDay: endOfWeek(new Date(), { weekStartsOn: 1 }),
  });
  const updateWeekChange = ({ firstDay, lastDay }) => {
    if (getYearFormatter(firstDay) !== getYearFormatter(week.firstDay)) {
      setWeek({
        firstDay,
        lastDay,
      });
    }
  };
  const handlePaginationChange = useCallback(({ page, pageSize }) => {
    setPageNumber(page);
    setPageSize(pageSize);
  }, []);
  const updateSelectedList = ({ id, selected }) => {
    setSeletedIds((prevRecords) => {
      const tmpSelectedRecords = prevRecords.slice();
      const prevIndex = tmpSelectedRecords.findIndex(
        (record) => record.id === id
      );
      if (prevIndex > -1 && !selected) {
        tmpSelectedRecords.splice(prevIndex, 1);
      } else {
        tmpSelectedRecords.push({ id, selected });
      }
      return tmpSelectedRecords;
    });
  };
  const fetchApprovalList = useCallback(
    async (payloadOptions) => {
      try {
        const { url, options } = getApprovalListServiceConfig(payloadOptions);
        const response = await performAction(url, options);
        if (Array.isArray(response?.Result)) {
          const formattedList = response.Result.map((approvalItem) => {
            const {
              TimesheetID,
              ResourceID,
              ResourceName,
              PendingHours,
              ProjectID,
              ProjectName,
              ClientName,
              VendorName,
              Status,
              TotalBillableHours,
              TotalNonBillableHours,
              TotalRecords,
            } = approvalItem;
            const approvalStatus = Status === "Submitted" ? "Pending" : Status;
            return {
              projectId: ProjectID,
              projectName: ProjectName,
              resource: ResourceName,
              resourceId: ResourceID,
              timesheetId: TimesheetID,
              client: ClientName,
              vendor: VendorName || "N/A",
              billableHours: TotalBillableHours || 0,
              nonBillableHours: TotalNonBillableHours || 0,
              pendingHours: PendingHours || 0,
              approvalStatus: approvalStatus,
              showActionButton: Status === TIMESHEET_ACTION_STATES.SUBMITTED,
              totalRecords: TotalRecords,
            };
          });
          const totalRecordsCount = formattedList[0]?.totalRecords;
          setTotalRecords(totalRecordsCount);
          return setApprovalList(formattedList);
        }
        throw new Error("Error Fetching list");
      } catch (e) {
        console.error("error fetching approval list");
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      }
    },
    [performAction, setToastInfo]
  );
  const updateTimesheetStatus = useCallback(
    async ({ ids, action, comments }) => {
      try {
        setShowApprovalModal(false);
        const { url, options } = getApproveRejectTimesheetServiceConfig(
          ids,
          action,
          comments
        );
        const response = await performAction(url, options);
        if (response?.StatusCode === 200) {
          if (chunkRef.current === "bulk") {
            setSeletedIds([]);
          }
          setApprovalList((prevList) => {
            const tmpList = prevList.slice();
            ids.forEach((id) => {
              const timesheetIdx = tmpList.findIndex(
                (listItem) => listItem.timesheetId === id
              );
              if (timesheetIdx > -1) {
                tmpList[timesheetIdx].approvalStatus = action;
                tmpList[timesheetIdx].showActionButton = false;
              }
            });
            return tmpList;
          });
          return setToastInfo({
            message: translate("labels.timsheetActionSuccessMsg"),
            type: "success",
            autoExpire: true,
          });
        }
        throw new Error("Unable to perform action please try again");
      } catch (e) {
        console.error("Error updating timesheet details");
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
        updateCount();
      }
    },
    [performAction, setToastInfo]
  );
  const handleToastClose = useCallback(() => {
    setToastInfo(null);
  }, [setToastInfo]);
  const onMoreAction = (ids, action, mode = "single") => {
    chunkRef.current = mode;
    setShowApprovalModal(true);
    setApprovalModalInfo({
      ids,
      action,
    });
  };
  useEffect(() => {
    const additionalParams = {
      ...week,
      page,
      pageSize,
    };
    fetchApprovalList(additionalParams);
  }, [week, page, pageSize, fetchApprovalList]);
  return {
    toastInfo,
    approvalList,
    page,
    pageSize,
    totalRecords,
    updateWeekChange,
    handlePaginationChange,
    isLoading,
    updateSelectedList,
    updateTimesheetStatus,
    showApprovalModal,
    setShowApprovalModal,
    selectedIds,
    approvalModalInfo,
    onMoreAction,
    handleToastClose,
    setApprovalModalInfo,
  };
};
